/**
 * FAQ accordion height animation.
 *
 * <details> can't animate open/closed on its own — the browser shows and hides
 * the content outright. This intercepts the summary click, animates the answer's
 * height, and only then flips the open state.
 *
 * The section renders each row with a shared `name` attribute so browsers close
 * the others natively when JS is unavailable. That native swap is instant and
 * happens before we can animate, so on init the attribute is moved to a data-
 * attribute and the exclusivity is handled here instead.
 *
 * Degrades to the browser's own instant open/close if this script never runs.
 */
(function () {
  var DURATION = 300;
  var EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function answerOf(item) {
    return item.querySelector('.jc-faq__answer');
  }

  function stopCurrent(item) {
    if (item._jcFaqAnimation) {
      item._jcFaqAnimation.cancel();
      item._jcFaqAnimation = null;
    }
  }

  /** Current rendered height, mid-animation included, so a reversal starts where it is. */
  function currentHeight(answer) {
    return answer.getBoundingClientRect().height;
  }

  function run(item, answer, from, to, onDone) {
    item._jcFaqAnimation = answer.animate(
      [{ height: from + 'px' }, { height: to + 'px' }],
      { duration: DURATION, easing: EASING }
    );
    item._jcFaqAnimation.onfinish = function () {
      item._jcFaqAnimation = null;
      onDone();
    };
  }

  function expand(item) {
    var answer = answerOf(item);
    if (!answer || reducedMotion()) {
      item.open = true;
      return;
    }

    var from = item.open ? currentHeight(answer) : 0;
    stopCurrent(item);

    item.open = true;
    var target = answer.getBoundingClientRect().height;

    // Pin the collapsed height in the same synchronous block as the measurement.
    // Without it the browser paints one frame at full height before the
    // animation's first frame lands — the flash that reads as jank.
    answer.style.height = from + 'px';

    run(item, answer, from, target, function () {
      answer.style.height = '';
    });
  }

  function collapse(item) {
    var answer = answerOf(item);
    if (!answer || reducedMotion()) {
      item.open = false;
      return;
    }

    var from = currentHeight(answer);
    stopCurrent(item);
    answer.style.height = from + 'px';

    run(item, answer, from, 0, function () {
      // Hold at zero across the open flip so no frame paints at full height
      answer.style.height = '0px';
      item.open = false;
      answer.style.height = '';
    });
  }

  function closeGroup(item) {
    var group = item.dataset.jcFaqGroup;
    if (!group) return;

    document
      .querySelectorAll('.jc-faq__item[data-jc-faq-group="' + group + '"][open]')
      .forEach(function (sibling) {
        if (sibling !== item) collapse(sibling);
      });
  }

  function onSummaryClick(event) {
    var item = event.currentTarget.closest('.jc-faq__item');
    if (!item) return;

    // The browser would toggle `open` immediately, leaving nothing to animate
    event.preventDefault();

    if (item.open) {
      collapse(item);
    } else {
      closeGroup(item);
      expand(item);
    }
  }

  function setup(item) {
    if (item.dataset.jcFaqReady) return;
    item.dataset.jcFaqReady = 'true';

    var group = item.getAttribute('name');
    if (group) {
      item.dataset.jcFaqGroup = group;
      item.removeAttribute('name');
    }

    var summary = item.querySelector('summary');
    if (summary) summary.addEventListener('click', onSummaryClick);
  }

  function scan() {
    document.querySelectorAll('.jc-faq__item').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();
